import { EnumProductAnalyticsEvent } from '@calm/evexia-analytics';
import { MediaProgram, MediaProgramGuide } from '@calm/evexia-web-sdk';
import { useTracking } from '@rhg/libs/analytics';
import { getScreenName } from '@rhg/libs/analytics/track-page-nav';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import { useMediaPreferences } from '@rhg/providers/media-preferences';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useProgramContext } from '../use-program-context';

interface TrackSessionEventProps {
    eventName: EnumProductAnalyticsEvent;
    guide: MediaProgramGuide;
    program?: MediaProgram;
    packClass?: string;
    percentCompleted?: number;
}

type TrackPackItemEventPackGuide =
    | {
          guide: MediaProgramGuide;
          packTitle?: string;
      }
    | { guide?: never; packTitle?: string };

interface TrackPackItemEventBase {
    eventName: EnumProductAnalyticsEvent;
    program?: MediaProgram;
}

type TrackPackItemEventProps = TrackPackItemEventBase &
    TrackPackItemEventPackGuide;

export const useTrackSessionEvent = () => {
    const { program: programFromContext } = useProgramContext({});
    const router = useRouter();
    const { track } = useTracking();
    const { isAuthenticated } = useAuth();

    const { isShowingSubtitles } = useMediaPreferences();

    const trackSessionEvent = useCallback(
        ({
            eventName,
            guide,
            program: providedProgram,
            packClass,
            percentCompleted,
        }: TrackSessionEventProps) => {
            const program = programFromContext ?? providedProgram;
            if (!guide) {
                logger.error(
                    'No guide provided',
                    { eventName, guide },
                    {
                        name: 'No guide provided',
                        message:
                            'useTrackSessionEvent: trackSessionEvent : No guide provided',
                    }
                );
                return;
            }

            // P2 analytics will come in a future ticket
            track(eventName, {
                GuideId: guide.id,
                GuideLanguage: program?.language,
                GuideLength: guide.totalDuration,
                GuideName: guide.title,
                IsClinicalProgram: program?.isClinical,
                IsTrailer: guide.isTrailer,
                IsUsingSubtitles: isShowingSubtitles,
                LoggedHourLocal: new Date().getHours(),
                MediaType: guide.assets[0].type,
                NarratorName: program?.narrator?.name,
                // SourceScreenIndex: 'REPLACE THIS',  // P2
                // SourceScreenItemIndex: guide.position,  // P2
                ProgramId: guide.programId,
                ProgramSessionNum: guide.position,
                ProgramName: program?.title,
                ContentType: program?.contentType?.toLowerCase(),
                PackClass: packClass,
                SourceScreenName: getScreenName(
                    router?.pathname,
                    isAuthenticated
                ),
                TrackURL: guide.assets[0].url,
                PercentCompleted: percentCompleted,
            });
        },
        [
            programFromContext,
            router?.pathname,
            isAuthenticated,
            track,
            isShowingSubtitles,
        ]
    );

    const trackPackItemEvent = useCallback(
        ({
            eventName,
            guide,
            packTitle,
            program: providedProgram,
        }: TrackPackItemEventProps) => {
            const program = programFromContext ?? providedProgram;

            if (!guide && !packTitle) {
                logger.error(
                    'No guide or packTitle provided',
                    { eventName, guide },
                    {
                        name: 'No guide or packTitle provided',
                        message:
                            'useTrackSessionEvent: trackPackItemEvent : No guide or pack provided',
                    }
                );
                return;
            }

            track(eventName, {
                PackName: packTitle,
                PackItemName: guide?.title,
                ProgramId: guide?.programId,
                ProgramName: program?.title,
                ProgramSessionNum: guide?.isTrailer ? 0 : guide?.position,
                ContentType: program?.contentType?.toLowerCase(),
                IsClinicalProgram: program?.isClinical,
                GuideId: guide?.id,
                GuideName: guide?.title,
                ScreenName: getScreenName(router?.pathname, isAuthenticated),
            });
        },
        [isAuthenticated, programFromContext, router?.pathname, track]
    );

    return { trackSessionEvent, trackPackItemEvent };
};
